import { Button } from "@mui/material";
import { useRef, useState, useEffect } from "react";
import { MoonLoader } from "react-spinners";

import { GetUserCourses } from "../../handler/coursehandler";
import growniverseImage from "../../Photos/Logo/growniverse-appicon-transparent.svg";

import Learning from "./Learning";
import MyCoursesDisplay from "./MyCoursesDisplay";

import "../../Design/Responsive.css";

interface Course {
    courseId: number;
    courseName: string;
    courseDescription: string;
    percentage: number;
    images: string;
    packageId: number;
    courseVideoCount: number;
    purchased: boolean;
    onServer: boolean;
    coursePrice?: number;
    serverName?: string;
    isSubscribed:boolean;
    startDate:string;
    endDate:string
}

interface CourseLearning {
    courseId: number;
    courseName: string;
    courseDescription: string;
    status: string;
    images: string;
    packageId: number;
}

const MyCourses = () => {
    const runOnce = useRef(false);

    /*
     * =========================================================
     * PAGE / MODULE
     * =========================================================
     */
    const [showModule, setShowModule] = useState("courses");

    /*
     * =========================================================
     * LOADER
     * =========================================================
     */
    const [loader, setLoader] = useState(true);

    /*
     * =========================================================
     * COURSES
     * =========================================================
     */
    const [courses, setCourses] = useState<Course[]>([]);

    /*
     * =========================================================
     * SELECTED COURSE
     * =========================================================
     */
    const [courseLearning, setCourseLearning] =
        useState<CourseLearning>({
            courseId: 0,
            courseName: "",
            courseDescription: "",
            status: "",
            images: "",
            packageId: 0,
        });

    /*
     * =========================================================
     * OPEN COURSE
     * =========================================================
     */
    const courseStartLearning = (course: Course) => {
        setCourseLearning({
            courseId: course.courseId,
            courseName: course.courseName,
            courseDescription: course.courseDescription,

            status:
                course.percentage > 0
                    ? "In Progress"
                    : "Not Started",

            images: course.images,
            packageId: course.packageId,
        });

        setShowModule("learning");
    };

    /*
     * =========================================================
     * LOAD MY COURSES
     * =========================================================
     */
    const MyCourse = async () => {
        if (runOnce.current) {
            return;
        }

        runOnce.current = true;

        try {
            setLoader(true);

            /*
             * -------------------------------------------------
             * GET USER PROFILE
             * -------------------------------------------------
             */
            const profile =
                localStorage.getItem("userProfile");

            if (!profile) {
                console.error(
                    "userProfile not found in localStorage"
                );

                setCourses([]);
                return;
            }

            let userProfile: any;

            try {
                userProfile = JSON.parse(profile);
            } catch (error) {
                console.error(
                    "Failed to parse userProfile:",
                    error
                );

                setCourses([]);
                return;
            }

            /*
             * -------------------------------------------------
             * PACKAGE ID
             * -------------------------------------------------
             */
            const currentPackageId =
                Number(
                    userProfile?.packageId ?? 0
                );

            /*
             * -------------------------------------------------
             * GET USER COURSES
             * -------------------------------------------------
             */
            const response =
                await GetUserCourses(
                    currentPackageId
                );

            if (
                response?.statusCode === 200
            ) {
                const apiCourses =
                    response?.data?.courses ?? [];

                /*
                 * -------------------------------------------------
                 * REMOVE DUPLICATES
                 * -------------------------------------------------
                 */
                const uniqueCourses =
                    apiCourses.filter(
                        (
                            course: Course,
                            index: number,
                            array: Course[]
                        ) =>
                            index ===
                            array.findIndex(
                                (
                                    item: Course
                                ) =>
                                    item.courseId ===
                                    course.courseId
                            )
                    );

                /*
                 * -------------------------------------------------
                 * FORCE PURCHASED
                 * -------------------------------------------------
                 */
                const purchasedCourses =
                    uniqueCourses.map(
                        (course: Course) => ({
                            ...course,
                            purchased: true,
                        })
                    );

                setCourses(
                    purchasedCourses
                );
            } else {
                console.error(
                    "Unable to load courses:",
                    response
                );

                setCourses([]);
            }
        } catch (error) {
            console.error(
                "Error loading My Courses:",
                error
            );

            setCourses([]);
        } finally {
            setLoader(false);
        }
    };

    /*
     * =========================================================
     * LOAD ON PAGE OPEN
     * =========================================================
     */
    useEffect(() => {
        MyCourse();
    }, []);

    /*
     * =========================================================
     * BACK TO COURSES
     * =========================================================
     */
    const backToCourses = () => {
        setShowModule("courses");
    };

    /*
     * =========================================================
     * RENDER
     * =========================================================
     */
    return (
        <section
            style={{
                minHeight: "100vh",
                width: "100%",
                background: "#000",
            }}
        >
            {/* =====================================================
                FUTURISTIC BLUE LOADER
            ====================================================== */}

            {loader && (
                <div
                    style={{
                        minHeight: "75vh",
                        width: "100%",
                        background:
                            "radial-gradient(circle at center, #123d73 0%, #071a33 40%, #020814 75%, #000 100%)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    {/* =========================================
                        BACKGROUND BLUE GLOW
                    ========================================== */}

                    <div
                        style={{
                            position: "absolute",
                            width: "350px",
                            height: "350px",
                            borderRadius: "50%",
                            background:
                                "radial-gradient(circle, rgba(0,150,255,0.28) 0%, rgba(0,100,255,0.12) 40%, transparent 70%)",
                            filter: "blur(10px)",
                            animation:
                                "pulseGlow 3s ease-in-out infinite",
                        }}
                    />

                    {/* =========================================
                        SECOND GLOW
                    ========================================== */}

                    <div
                        style={{
                            position: "absolute",
                            width: "500px",
                            height: "500px",
                            borderRadius: "50%",
                            border:
                                "1px solid rgba(54,184,255,0.08)",
                            boxShadow:
                                "0 0 80px rgba(0,150,255,0.12)",
                            animation:
                                "pulseOuterRing 4s ease-in-out infinite",
                        }}
                    />

                    {/* =========================================
                        FUTURISTIC RING
                    ========================================== */}

                    <div
                        style={{
                            position: "absolute",
                            width: "170px",
                            height: "170px",
                            borderRadius: "50%",
                            border:
                                "1px solid rgba(54,184,255,0.30)",
                            boxShadow:
                                "0 0 30px rgba(0,150,255,0.25)",
                            animation:
                                "pulseRing 2s ease-in-out infinite",
                        }}
                    />

                    {/* =========================================
                        OUTER LOADER RING
                    ========================================== */}

                    <div
                        style={{
                            position: "absolute",
                            width: "145px",
                            height: "145px",
                            borderRadius: "50%",
                            border:
                                "1px solid rgba(54,184,255,0.15)",
                            animation:
                                "rotateRing 8s linear infinite",
                        }}
                    />

                    {/* =========================================
                        LOGO
                    ========================================== */}

                    <img
                        src={growniverseImage}
                        alt="Loading..."
                        width="100px"
                        style={{
                            position: "absolute",
                            zIndex: 5,
                            filter:
                                "drop-shadow(0 0 10px rgba(0,170,255,0.9)) drop-shadow(0 0 25px rgba(0,130,255,0.6))",
                            animation:
                                "logoGlow 2.5s ease-in-out infinite",
                        }}
                    />

                    {/* =========================================
                        LOADER
                    ========================================== */}

                    <div
                        style={{
                            position: "relative",
                            zIndex: 4,
                        }}
                    >
                        <MoonLoader
                            size={120}
                            color="#36b8ff"
                        />
                    </div>

                    {/* =========================================
                        LOADING TEXT
                    ========================================== */}

                    <div
                        style={{
                            position: "absolute",
                            bottom: "70px",
                            left: "0",
                            right: "0",
                            textAlign: "center",
                            color: "#75d4ff",
                            fontSize: "13px",
                            fontWeight: 500,
                            letterSpacing: "3px",
                            textTransform: "uppercase",
                            textShadow:
                                "0 0 12px rgba(54,184,255,0.8)",
                            animation:
                                "textPulse 2s ease-in-out infinite",
                        }}
                    >
                        Loading Courses
                    </div>

                    {/* =========================================
                        BACKGROUND GRID
                    ========================================== */}

                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            backgroundImage:
                                "linear-gradient(rgba(54,184,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(54,184,255,0.035) 1px, transparent 1px)",
                            backgroundSize:
                                "45px 45px",
                            maskImage:
                                "radial-gradient(circle at center, black 20%, transparent 75%)",
                            WebkitMaskImage:
                                "radial-gradient(circle at center, black 20%, transparent 75%)",
                            pointerEvents: "none",
                        }}
                    />
                </div>
            )}

            {/* =====================================================
                CONTENT
            ====================================================== */}

            {!loader && (
                <>
                    {/* =================================================
                        MY COURSES
                    ================================================== */}

                    {showModule === "courses" && (
                        <div
                            style={{
                                minHeight: "100vh",
                                background:
                                    "radial-gradient(circle at top, #1c1c1c 0%, #080808 45%, #000 100%)",
                                padding:
                                    "30px 20px 50px",
                            }}
                        >
                            <div
                                style={{
                                    width: "100%",
                                    maxWidth: "1400px",
                                    margin: "0 auto",
                                }}
                            >
                                {/* -----------------------------------------
                                    HEADER
                                ------------------------------------------ */}

                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent:
                                            "space-between",
                                        alignItems:
                                            "center",
                                        gap: "20px",
                                        marginBottom:
                                            "30px",
                                        flexWrap:
                                            "wrap",
                                    }}
                                >
                                    <div>
                                        <h1
                                            style={{
                                                margin: 0,
                                                color: "#fff",
                                                fontSize:
                                                    "32px",
                                                fontWeight: 700,
                                                letterSpacing:
                                                    "0.5px",
                                            }}
                                        >
                                            My Courses
                                        </h1>

                                        <p
                                            style={{
                                                margin:
                                                    "8px 0 0",
                                                color:
                                                    "#a8a8a8",
                                                fontSize:
                                                    "15px",
                                            }}
                                        >
                                            Continue your
                                            learning journey
                                        </p>
                                    </div>

                                    {/* COURSE COUNT */}

                                    <div
                                        style={{
                                            background:
                                                "rgba(255,255,255,0.08)",
                                            border:
                                                "1px solid rgba(255,255,255,0.12)",
                                            borderRadius:
                                                "30px",
                                            padding:
                                                "10px 20px",
                                            color:
                                                "#fff",
                                            fontSize:
                                                "14px",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {courses.length}{" "}
                                        {courses.length ===
                                        1
                                            ? "Course"
                                            : "Courses"}
                                    </div>
                                </div>

                                {/* =================================================
                                    NO COURSES
                                ================================================== */}

                                {courses.length ===
                                    0 && (
                                    <div
                                        style={{
                                            minHeight:
                                                "400px",
                                            display:
                                                "flex",
                                            flexDirection:
                                                "column",
                                            justifyContent:
                                                "center",
                                            alignItems:
                                                "center",
                                            background:
                                                "rgba(255,255,255,0.04)",
                                            border:
                                                "1px solid rgba(255,255,255,0.08)",
                                            borderRadius:
                                                "20px",
                                            padding:
                                                "40px",
                                            textAlign:
                                                "center",
                                        }}
                                    >
                                        <div
                                            style={{
                                                width:
                                                    "80px",
                                                height:
                                                    "80px",
                                                borderRadius:
                                                    "50%",
                                                background:
                                                    "rgba(245,180,95,0.12)",
                                                display:
                                                    "flex",
                                                justifyContent:
                                                    "center",
                                                alignItems:
                                                    "center",
                                                marginBottom:
                                                    "20px",
                                            }}
                                        >
                                            <i
                                                className="fa-solid fa-book-open"
                                                style={{
                                                    fontSize:
                                                        "32px",
                                                    color:
                                                        "#f5b45f",
                                                }}
                                            />
                                        </div>

                                        <h3
                                            style={{
                                                color:
                                                    "#fff",
                                                margin:
                                                    "0 0 10px",
                                            }}
                                        >
                                            No Courses
                                            Available
                                        </h3>

                                        <p
                                            style={{
                                                color:
                                                    "#999",
                                                margin: 0,
                                                maxWidth:
                                                    "450px",
                                                lineHeight:
                                                    "1.6",
                                            }}
                                        >
                                            You don't have
                                            any courses
                                            available at the
                                            moment.
                                        </p>
                                    </div>
                                )}

                                {/* =================================================
                                    COURSE LIST
                                ================================================== */}

                                {courses.length >
                                    0 && (
                                    <MyCoursesDisplay
                                        courses={
                                            courses
                                        }
                                        courseStartLearning={
                                            courseStartLearning
                                        }
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    {/* =================================================
                        LEARNING / VIDEO
                    ================================================== */}

                    {showModule ===
                        "learning" && (
                        <div
                            style={{
                                minHeight:
                                    "100vh",
                                width: "100%",
                                background:
                                    "#0a0a0a",
                                paddingBottom:
                                    "30px",
                            }}
                        >
                            {/* -----------------------------------------
                                LEARNING HEADER
                            ------------------------------------------ */}

                            <div
                                style={{
                                    position:
                                        "sticky",
                                    top: 0,
                                    zIndex: 50,
                                    background:
                                        "rgba(0,0,0,0.92)",
                                    backdropFilter:
                                        "blur(10px)",
                                    borderBottom:
                                        "1px solid rgba(255,255,255,0.1)",
                                    padding:
                                        "12px 20px",
                                    display:
                                        "flex",
                                    alignItems:
                                        "center",
                                    gap: "15px",
                                }}
                            >
                                <Button
                                    onClick={
                                        backToCourses
                                    }
                                    style={{
                                        minWidth:
                                            "45px",
                                        width: "45px",
                                        height:
                                            "45px",
                                        borderRadius:
                                            "50%",
                                        color:
                                            "#fff",
                                        background:
                                            "#252525",
                                        padding: 0,
                                    }}
                                >
                                    <i className="fa-solid fa-arrow-left" />
                                </Button>

                                <div
                                    style={{
                                        minWidth: 0,
                                    }}
                                >
                                    <div
                                        style={{
                                            color:
                                                "#fff",
                                            fontSize:
                                                "17px",
                                            fontWeight:
                                                600,
                                            overflow:
                                                "hidden",
                                            textOverflow:
                                                "ellipsis",
                                            whiteSpace:
                                                "nowrap",
                                        }}
                                    >
                                        {courseLearning.courseName ||
                                            "Course Learning"}
                                    </div>

                                    <div
                                        style={{
                                            color:
                                                "#888",
                                            fontSize:
                                                "12px",
                                            marginTop:
                                                "3px",
                                        }}
                                    >
                                        Continue Learning
                                    </div>
                                </div>
                            </div>

                            {/* -----------------------------------------
                                VIDEO / LEARNING COMPONENT
                            ------------------------------------------ */}

                            <div
                                style={{
                                    width: "100%",
                                    padding:
                                        "10px 0",
                                }}
                            >
                                <Learning
                                    courseId={
                                        courseLearning.courseId
                                    }
                                />
                            </div>

                            {/* -----------------------------------------
                                BACK BUTTON
                            ------------------------------------------ */}

                            <div
                                style={{
                                    padding:
                                        "10px 20px",
                                }}
                            >
                                <Button
                                    onClick={
                                        backToCourses
                                    }
                                    style={{
                                        borderRadius:
                                            "25px",
                                        color:
                                            "#fff",
                                        background:
                                            "#292929",
                                        padding:
                                            "9px 20px",
                                        textTransform:
                                            "none",
                                    }}
                                >
                                    <i
                                        className="fa-solid fa-arrow-left"
                                        style={{
                                            marginRight:
                                                "8px",
                                        }}
                                    />

                                    Back to My Courses
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </section>
    );
};

export default MyCourses;